import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Bell, PushPin, Trophy, Sparkle } from "@phosphor-icons/react";

/**
 * /signals/:sid — the individual Signal detail view.
 *
 * Parses the section-marker format (── SECTION ──) written by
 * release_notes.py and renders each section as a styled group.
 * Also renders any admin-uploaded image as a full-width hero.
 */
const TYPE_LABEL = {
  announcement: "Announcement",
  update: "Update",
  event: "Event",
  championship: "Championship",
  mode: "Mode",
  prize: "Prize",
};

const TYPE_GRADIENT = {
  announcement: "from-nk-gold/30 via-nk-gold/10 to-transparent",
  update:       "from-emerald-500/30 via-emerald-500/10 to-transparent",
  event:        "from-sky-500/30 via-sky-500/10 to-transparent",
  championship: "from-amber-500/40 via-amber-500/12 to-transparent",
  mode:         "from-violet-500/30 via-violet-500/10 to-transparent",
  prize:        "from-nk-gold/40 via-nk-gold/12 to-transparent",
};

export default function SignalDetail() {
  const { sid } = useParams();
  const [sig, setSig] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/signals/${sid}`)
       .then((r) => setSig(r.data))
       .catch((e) => setErr(e?.response?.data?.detail || "Signal not found"));
  }, [sid]);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="signal-detail-error">
        <BackLink />
        <div className="nk-card p-10 text-center mt-4">
          <Bell size={36} weight="duotone" className="text-white/25 mx-auto mb-3" />
          <p className="text-white/70">{err}</p>
        </div>
      </div>
    );
  }
  if (!sig) return <div className="p-6 text-white/45 text-sm">Loading…</div>;

  const sections = parseSections(sig.body);
  const gradient = TYPE_GRADIENT[sig.signal_type] || TYPE_GRADIENT.update;

  return (
    <article
      className="max-w-2xl mx-auto p-4 sm:p-6 pb-16 space-y-6"
      data-testid="signal-detail-page"
    >
      <BackLink />

      {/* Hero */}
      <header className="relative rounded-lg overflow-hidden">
        {sig.image_data_url ? (
          <div className="relative aspect-[16/9] bg-black/30">
            {/* eslint-disable-next-line */}
            <img src={sig.image_data_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          </div>
        ) : (
          <div className={`relative aspect-[16/8] bg-gradient-to-br ${gradient} flex items-center justify-center`}>
            <span className="font-display text-[72px] sm:text-[96px] leading-none select-none" aria-hidden>
              {sig.hero_emoji || "✨"}
            </span>
          </div>
        )}
        {sig.pinned && (
          <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-nk-charcoal bg-nk-gold px-2.5 py-1 rounded-full font-medium">
            <PushPin size={10} weight="fill" /> Pinned
          </div>
        )}
      </header>

      {/* Meta + title */}
      <div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-nk-gold">
            {TYPE_LABEL[sig.signal_type] || sig.signal_type}
          </span>
          <span className="text-white/25">·</span>
          <span className="text-[10px] text-white/45 font-mono">
            {formatDate(sig.published_at)}
          </span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight leading-[1.1] text-white">
          {sig.title}
        </h1>
        {sig.summary && (
          <p className="mt-4 text-base sm:text-lg text-white/70 leading-relaxed">
            {sig.summary}
          </p>
        )}
      </div>

      <hr className="border-t border-white/8" />

      {/* Body — sectioned */}
      <div className="space-y-8" data-testid="signal-detail-body">
        {sections.map((sec, idx) => (
          <Section key={idx} title={sec.title} paras={sec.paras} />
        ))}
      </div>

      {/* CTA */}
      {sig.cta_href && sig.cta_label && (
        <div className="pt-4">
          <Link
            to={sig.cta_href}
            data-testid="signal-detail-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-sm bg-nk-gold text-nk-charcoal font-medium text-sm hover:bg-nk-gold/85 transition-colors"
          >
            {sig.cta_label} <ArrowRight size={13} weight="bold" />
          </Link>
        </div>
      )}
    </article>
  );
}

/* -------- helpers -------- */

function BackLink() {
  return (
    <Link
      to="/signals"
      data-testid="signal-detail-back"
      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/50 hover:text-nk-gold transition-colors"
    >
      <ArrowLeft size={11} weight="bold" /> All signals
    </Link>
  );
}

/**
 * Split raw body into sections.
 * Format written by release_notes._bundle_and_publish:
 *
 *     ── SECTION TITLE ──
 *
 *     paragraph 1
 *     • bullet
 *     paragraph 2
 *
 *     ── ANOTHER ──
 *     …
 */
function parseSections(raw) {
  if (!raw) return [];
  const sections = [];
  // Split on the ── header ── marker; keep header as the section title.
  const parts = raw.split(/(?:^|\n)──\s*(.+?)\s*──\s*\n/);
  // If no markers found, treat the whole body as one untitled section
  if (parts.length === 1) {
    return [{ title: null, paras: paragraphsFrom(raw) }];
  }
  // parts = [preludeBeforeFirstMarker, title1, body1, title2, body2, ...]
  const [prelude, ...rest] = parts;
  if (prelude && prelude.trim()) {
    sections.push({ title: null, paras: paragraphsFrom(prelude) });
  }
  for (let i = 0; i < rest.length; i += 2) {
    const title = titleCase(rest[i]);
    const body = rest[i + 1] || "";
    sections.push({ title, paras: paragraphsFrom(body) });
  }
  return sections;
}

function titleCase(s) {
  return s.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
}

function paragraphsFrom(text) {
  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function Section({ title, paras }) {
  return (
    <section>
      {title && (
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={12} weight="fill" className="text-nk-gold" />
          <h2 className="font-display text-sm uppercase tracking-widest text-nk-gold">
            {title}
          </h2>
        </div>
      )}
      <div className="space-y-3">
        {paras.map((p, i) => (
          <ParagraphOrBullets key={i} text={p} />
        ))}
      </div>
    </section>
  );
}

function ParagraphOrBullets({ text }) {
  // If any line starts with •, render as a bullet list.
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const bulletCount = lines.filter((l) => l.startsWith("•") || l.startsWith("- ")).length;
  if (bulletCount >= 1 && bulletCount === lines.length) {
    return (
      <ul className="space-y-2">
        {lines.map((l, i) => (
          <li key={i} className="flex gap-2.5 text-sm text-white/80 leading-relaxed">
            <span className="text-nk-gold/70 mt-0.5">•</span>
            <span>{l.replace(/^[•\-]\s*/, "")}</span>
          </li>
        ))}
      </ul>
    );
  }
  // Mixed prose + bullets — render each line, indenting bullets
  if (bulletCount) {
    return (
      <div className="space-y-1.5">
        {lines.map((l, i) => (
          l.startsWith("•") || l.startsWith("- ") ? (
            <p key={i} className="text-sm text-white/80 leading-relaxed pl-4 relative">
              <span className="absolute left-0 text-nk-gold/70">•</span>
              {l.replace(/^[•\-]\s*/, "")}
            </p>
          ) : (
            <p key={i} className="text-base text-white/85 leading-relaxed">{l}</p>
          )
        ))}
      </div>
    );
  }
  return <p className="text-base text-white/85 leading-relaxed">{text}</p>;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}
